import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBBadge,
  MDBSpinner,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

import BatchFormModal from "./BatchFormModal";
import BatchFromZoneModal from "./BatchFromZoneModal";
import TicketsModal from "./TicketsModal";
import BatchAssignmentsModal from "./BatchAssignmentsModal";
import BatchZonesModal from "./BatchZonesModal";
import SeatMapView from "./SeatMapView";

/**
 * BatchesModal — listado de lotes de una sesión, con CRUD y acceso a tickets.
 *
 * Props:
 *  - show
 *  - sesion: { idRepresentacionFecha, startDatetime, ... }
 *  - funcion: FuncionListItemDTO (completo, con idVenue y name)
 *  - funcionName: string (sólo display, fallback si funcion no está disponible)
 *  - onClose
 *  - canManage: boolean — si false, oculta acciones de gestión (modo vendedor).
 *  - canSell: boolean
 */
class BatchesModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      batches: [],
      loading: false,
      formOpen: false,
      formBatch: null,
      fromZoneOpen: false,
      ticketsBatch: null,
      confirmDelete: null,
      forceDelete: false,
      assignmentsBatch: null,
      zonesBatch: null,
      activeTab: "lotes",
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.setState({ activeTab: "lotes" });
      this.loadBatches();
    }
  }

  loadBatches = () => {
    const { sesion, canManage, canSell } = this.props;
    if (!sesion) return;
    this.setState({ loading: true });
    const isManager = canManage !== false;
    const isSellOnly = !isManager && canSell === true;
    const fetchPromise = isSellOnly
      ? doFetchJson("TEAN", "getMyAssignedBatches")
      : doFetchJson("TEAN", "getTicketBatches?idRepresentacionFecha=" + sesion.idRepresentacionFecha);
    fetchPromise
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          const all = Array.isArray(json.DATA) ? json.DATA : [];
          // En modo vendedor, getMyAssignedBatches devuelve todos los lotes del usuario;
          // filtrar aquí para mostrar solo los de la sesión actual.
          const batches = isSellOnly
            ? all.filter((b) => b.idRepresentacionFecha === sesion.idRepresentacionFecha)
            : all;
          this.setState({ batches });
        } else {
          this.setState({ batches: [] });
        }
      })
      .catch(() => this.setState({ batches: [] }))
      .finally(() => this.setState({ loading: false }));
  };

  handleClose = () => {
    if (this.props.onClose) this.props.onClose();
  };

  openCreate = () => this.setState({ formOpen: true, formBatch: null });
  openEdit = (b) => this.setState({ formOpen: true, formBatch: b });
  closeForm = () => this.setState({ formOpen: false, formBatch: null });

  onSaved = () => {
    this.closeForm();
    this.loadBatches();
  };

  openFromZone = () => this.setState({ fromZoneOpen: true });
  closeFromZone = () => this.setState({ fromZoneOpen: false });
  onFromZoneCreated = () => {
    this.setState({ fromZoneOpen: false });
    this.loadBatches();
  };

  openTickets = (b) => this.setState({ ticketsBatch: b });
  closeTickets = () => this.setState({ ticketsBatch: null });

  openAssignments = (b) => this.setState({ assignmentsBatch: b });
  closeAssignments = () => this.setState({ assignmentsBatch: null });

  openZones = (b) => this.setState({ zonesBatch: b });
  closeZones = () => this.setState({ zonesBatch: null });
  onZonesSaved = () => { this.loadBatches(); };

  askDelete = (b) => this.setState({ confirmDelete: b, forceDelete: false });
  cancelDelete = () => this.setState({ confirmDelete: null, forceDelete: false });

  confirmDeleteNow = () => {
    const { confirmDelete, forceDelete } = this.state;
    if (!confirmDelete) return;
    const qs = forceDelete ? "?force=true" : "";
    doFetchJson("TEAN", "deleteTicketBatch/" + confirmDelete.idBatch + qs, { method: "DELETE" })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: "Lote eliminado", type: "success" });
          this.setState({ confirmDelete: null });
          this.loadBatches();
        } else {
          toast.show({
            message: (json && json.MESSAGE) || "No se pudo eliminar el lote",
            type: "danger",
          });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }));
  };

  formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return iso;
    }
  };

  render() {
    const { show, sesion, funcion, funcionName, canManage, canSell } = this.props;
    const isManager = canManage !== false; // por defecto true
    const isSellOnly = !isManager && canSell === true;
    const { batches, loading, formOpen, formBatch, fromZoneOpen, ticketsBatch, confirmDelete, forceDelete, assignmentsBatch, zonesBatch, activeTab } = this.state;
    const displayName = (funcion && funcion.name) || funcionName;
    const hasVenue = funcion && funcion.idVenue;
    if (!sesion) return null;

    return (
      <>
        <MDBModal show={show} onHide={this.handleClose} tabIndex="-1">
          <MDBModalDialog centered scrollable size="xl">
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>
                  <MDBIcon fas icon="ticket-alt" className="me-2" />
                  Lotes de entradas
                </MDBModalTitle>
                <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
              </MDBModalHeader>
              <MDBModalBody>
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                  <div>
                    {displayName && <strong className="me-2">{displayName}</strong>}
                    <span className="text-muted small">
                      <MDBIcon far icon="clock" className="me-1" />
                      {this.formatDate(sesion.startDatetime)}
                    </span>
                    {hasVenue && (
                      <span className="text-muted small ms-2">
                        <MDBIcon fas icon="building" className="me-1" />
                        {funcion.venueName || "Sala asignada"}
                      </span>
                    )}
                  </div>
                  {activeTab === "lotes" && isManager && (
                    <div className="d-flex gap-2">
                      <MDBBtn color="primary" size="sm" onClick={this.openCreate}>
                        <MDBIcon fas icon="plus" className="me-1" />
                        Nuevo lote
                      </MDBBtn>
                      {hasVenue && (
                        <MDBBtn color="info" size="sm" onClick={this.openFromZone}>
                          <MDBIcon fas icon="layer-group" className="me-1" />
                          Desde zona
                        </MDBBtn>
                      )}
                    </div>
                  )}
                </div>

                {/* Tabs: solo si hay sala asignada */}
                {hasVenue && (
                  <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                      <button
                        className={`nav-link${activeTab === "lotes" ? " active" : ""}`}
                        onClick={() => this.setState({ activeTab: "lotes" })}
                      >
                        <MDBIcon fas icon="list" className="me-1" />
                        Lotes
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link${activeTab === "mapa" ? " active" : ""}`}
                        onClick={() => this.setState({ activeTab: "mapa" })}
                      >
                        <MDBIcon fas icon="th" className="me-1" />
                        Mapa de sala
                      </button>
                    </li>
                  </ul>
                )}

                {/* Mapa de sala */}
                {activeTab === "mapa" && hasVenue && (
                  <SeatMapView
                    idVenue={funcion.idVenue}
                    idRepresentacionFecha={sesion.idRepresentacionFecha}
                    canManage={isManager}
                    canSell={canSell === true}
                    onRefreshBatches={this.loadBatches}
                  />
                )}

                {activeTab === "lotes" && loading ? (
                  <div className="text-center py-4">
                    <MDBSpinner />
                  </div>
                ) : activeTab === "lotes" && batches.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    Esta sesión no tiene lotes todavía. Crea el primero con el botón superior.
                  </div>
                ) : activeTab === "lotes" ? (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th>Lote</th>
                          <th>Tipo</th>
                          <th className="text-end">Precio</th>
                          <th className="text-center">Disponibles</th>
                          <th className="text-center">Vendidas</th>
                          <th className="text-center">Canceladas</th>
                          <th className="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batches.map((b) => (
                          <tr key={b.idBatch}>
                            <td>
                              <strong>{b.name}</strong>
                              {b.notes && (
                                <div className="text-muted small">{b.notes}</div>
                              )}
                            </td>
                            <td>
                              {b.numbered ? (
                                <MDBBadge color="info">
                                  Numerado {b.numberStart}–{b.numberEnd}
                                </MDBBadge>
                              ) : (
                                <MDBBadge color="secondary">
                                  No numerado ({b.totalTickets})
                                </MDBBadge>
                              )}
                            </td>
                            <td className="text-end">
                              {b.price != null ? `${b.price} €` : "—"}
                            </td>
                            <td className="text-center">
                              <MDBBadge color="success" pill>
                                {b.countAvailable != null ? b.countAvailable : 0}
                              </MDBBadge>
                            </td>
                            <td className="text-center">
                              <MDBBadge color="primary" pill>
                                {b.countSold != null ? b.countSold : 0}
                              </MDBBadge>
                            </td>
                            <td className="text-center">
                              <MDBBadge color="danger" pill>
                                {b.countCancelled != null ? b.countCancelled : 0}
                              </MDBBadge>
                            </td>
                            <td className="text-end">
                              <MDBBtn
                                size="sm"
                                color="light"
                                onClick={() => this.openTickets(b)}
                                className="me-1"
                                title="Ver entradas"
                              >
                                <MDBIcon fas icon="list" />
                              </MDBBtn>
                              {isManager && (
                                <>
                                  <MDBBtn
                                    size="sm"
                                    color="light"
                                    onClick={() => this.openAssignments(b)}
                                    className="me-1"
                                    title="Asignar vendedores"
                                  >
                                    <MDBIcon fas icon="user-tag" />
                                  </MDBBtn>
                                  {hasVenue && (
                                    <MDBBtn
                                      size="sm"
                                      color={b.assignedZones && b.assignedZones.length > 0 ? "primary" : "light"}
                                      onClick={() => this.openZones(b)}
                                      className="me-1"
                                      title="Asignar zonas de sala"
                                    >
                                      <MDBIcon fas icon="vector-square" />
                                      {b.assignedZones && b.assignedZones.length > 0 && (
                                        <span className="ms-1 badge bg-white text-primary" style={{ fontSize: "0.65rem" }}>
                                          {b.assignedZones.length}
                                        </span>
                                      )}
                                    </MDBBtn>
                                  )}
                                  <MDBBtn
                                    size="sm"
                                    color="light"
                                    onClick={() => this.openEdit(b)}
                                    className="me-1"
                                    title="Editar"
                                  >
                                    <MDBIcon fas icon="pen" />
                                  </MDBBtn>
                                  <MDBBtn
                                    size="sm"
                                    color="light"
                                    onClick={() => this.askDelete(b)}
                                    title="Eliminar"
                                  >
                                    <MDBIcon fas icon="trash" className="text-danger" />
                                  </MDBBtn>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="secondary" outline onClick={this.handleClose}>
                  Cerrar
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        <BatchFormModal
          show={formOpen}
          batch={formBatch}
          idRepresentacionFecha={sesion.idRepresentacionFecha}
          onClose={this.closeForm}
          onSaved={this.onSaved}
        />

        <BatchFromZoneModal
          show={fromZoneOpen}
          idVenue={funcion ? funcion.idVenue : null}
          idRepresentacionFecha={sesion.idRepresentacionFecha}
          onClose={this.closeFromZone}
          onCreated={this.onFromZoneCreated}
        />

        <TicketsModal
          show={!!ticketsBatch}
          batch={ticketsBatch}
          canManage={isManager}
          onClose={this.closeTickets}
          onTicketChanged={this.loadBatches}
        />

        <BatchAssignmentsModal
          show={!!assignmentsBatch}
          batch={assignmentsBatch}
          onClose={this.closeAssignments}
        />

        <BatchZonesModal
          show={!!zonesBatch}
          batch={zonesBatch}
          idVenue={this.props.funcion ? this.props.funcion.idVenue : null}
          onClose={this.closeZones}
          onSaved={this.onZonesSaved}
        />

        <MDBModal show={!!confirmDelete} onHide={this.cancelDelete} tabIndex="-1">
          <MDBModalDialog centered>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>
                  <MDBIcon fas icon="exclamation-triangle" className="text-warning me-2" />
                  Eliminar lote
                </MDBModalTitle>
                <MDBBtn className="btn-close" color="none" onClick={this.cancelDelete} />
              </MDBModalHeader>
              <MDBModalBody>
                ¿Eliminar el lote{" "}
                <strong>"{confirmDelete ? confirmDelete.name : ""}"</strong>?
                {confirmDelete && confirmDelete.countSold > 0 && (
                  <div className="alert alert-warning mt-3 mb-2 py-2 small">
                    <MDBIcon fas icon="exclamation-circle" className="me-1" />
                    <strong>Este lote tiene {confirmDelete.countSold} entrada(s) vendida(s).</strong>
                    {" "}Si continúas, los registros de venta quedarán sin lote activo.
                    <div className="form-check mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="chkForceDelete"
                        checked={forceDelete}
                        onChange={(e) => this.setState({ forceDelete: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="chkForceDelete">
                        Entiendo las consecuencias, borrar igualmente
                      </label>
                    </div>
                  </div>
                )}
                {(!confirmDelete || confirmDelete.countSold === 0) && (
                  <div className="text-muted small mt-2">
                    Esta acción no se puede deshacer.
                  </div>
                )}
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="secondary" outline onClick={this.cancelDelete}>
                  Cancelar
                </MDBBtn>
                <MDBBtn
                  color="danger"
                  onClick={this.confirmDeleteNow}
                  disabled={confirmDelete && confirmDelete.countSold > 0 && !forceDelete}
                >
                  <MDBIcon fas icon="trash" className="me-1" />
                  Eliminar
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      </>
    );
  }
}

export default BatchesModal;
